import { ToasterToast } from '@ais-ui/ui-core';
import { capitalizeFirstLetter } from './capitalizeFirstLetter';
import { Messages } from '@ais-ui/locale-management';

type MutationType = 'create' | 'edit' | 'delete' | 'change' | 'send';

export const readErrorBody = async (error: any) => {
  try {
    const responseBody = error.response?.body;

    if (responseBody instanceof ReadableStream) {
      const reader = responseBody.getReader();
      const { value } = await reader.read();
      const text = new TextDecoder().decode(value);
      return JSON.parse(text);
    }

    return null;
  } catch (e) {
    console.error('Error reading error body:', e);
    return null;
  }
};

const mutationPastTense: Record<MutationType, string> = {
  create: 'created',
  edit: 'edited',
  delete: 'deleted',
  change: 'changed',
  send: 'sent',
};

export const mutateCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  entity: string,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void,
  customErrorMessages?: { [key: number]: string }
) => {
  const successClassName = `${mutation}-${entity}-success-toast`;
  const errorClassName = `${mutation}-${entity}-error-toast`;

  const successMessage = capitalizeFirstLetter(
    messages.genericToastGenericSuccessMessage
      .replace('{mutationPastTense}', mutationPastTense[mutation])
      .replace('{entity}', entity)
  );

  const duplicatedDataErrorMessage =
    messages.genericToastGenericErrorDuplicatedDataMessage.replace(
      '{entity}',
      entity
    );

  const permissionErrorMessage = messages.genericToastPermissionErrorMessage
    .replace('{mutation}', mutation)
    .replace('{entity}', entity);

  const defaultErrorMessage = messages.genericToastGenericErrorMessage
    .replace('{mutation}', mutation)
    .replace('{entity}', entity);

  return {
    onSuccess: () => {
      toast({
        className: successClassName,
        title: messages.genericToastTitleSuccess,
        description: successMessage,
      });
      if (setShowDialog) {
        setShowDialog(false);
      }
    },
    onError: (error: any) => {
      const statusCode = error.response?.status;
      const customErrorMessage = customErrorMessages?.[statusCode];
      if (statusCode === 403) {
        toast({
          className: errorClassName,
          title: messages.genericToastTitleForbidden,
          description: permissionErrorMessage,
          variant: 'destructive',
        });
      } else if (statusCode === 409) {
        toast({
          className: errorClassName,
          title: messages.genericToastTitleError,
          description: duplicatedDataErrorMessage,
          variant: 'destructive',
        });
      } else {
        toast({
          className: errorClassName,
          title: messages.genericToastTitleError,
          description: customErrorMessage || defaultErrorMessage,
          variant: 'destructive',
        });
      }
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  };
};

export const shiplistCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'shiplist',
    setIsSubmitting,
    setShowDialog
  );

  const extendedOnError = (error: any) => {
    const statusCode = error.response?.status;
    const errorMessage = error.response?.message;

    if (statusCode === 400) {
      if (errorMessage.includes('Invalid MMSI number, must be 9 digits')) {
        toast({
          title: messages.genericToastTitleError,
          description: 'Invalid MMSI number, must be 9 digits',
          variant: 'destructive',
        });
        return;
      }
    }

    callBacks.onError(error);
  };

  const extendedCallbacks = {
    ...callBacks,
    onError: extendedOnError,
  };

  return extendedCallbacks;
};

export const shapeCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'shape',
    setIsSubmitting,
    setShowDialog
  );

  const extendedOnError = async (error: any) => {
    const statusCode = error.response?.status;
    const errorData = await readErrorBody(error);

    if (statusCode === 400 && errorData?.message?.includes('self-crossings')) {
      toast({
        title: messages.genericToastTitleError,
        description: 'GeoJSON contains self-crossings',
        variant: 'destructive',
      });
      return;
    }

    callBacks.onError(error);
  };

  const extendedCallbacks = {
    ...callBacks,
    onError: extendedOnError,
  };

  return extendedCallbacks;
};

export const filterCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  reApplyFilters: () => void,
  setShowDialog?: (showDialog: boolean) => void
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'filter',
    setIsSubmitting,
    setShowDialog
  );

  const extendedOnSuccess = async () => {};

  const extendedCallbacks = {
    ...callBacks,
    onSuccess: extendedOnSuccess,
  };

  return extendedCallbacks;
};
